import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  type Theme,
  Box,
  type PaperProps,
} from '@mui/material';
import { forwardRef } from 'react';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import { bloxMarketingColors } from 'themes/constants';
// Local.
import WidgetError from 'components/Dashboard/components/WidgetError';
import WidgetLoadingIndicator from 'components/Dashboard/components/WidgetLoadingIndicator';
import WidgetMask from 'components/Dashboard/components/WidgetMask';
import { useDashboardContext } from 'screens/dashboard/Dashboard';
import { useWidgetHashTarget } from 'components/Dashboard/components/useWidgetHashTarget';
import WidgetHeader from 'components/Dashboard/components/WidgetHeader';
import { TKpiProps } from 'components/Dashboard/types/kpi';

interface KpiPaperProps extends PaperProps {
  isChild?: boolean;
}

const KpiPaper = forwardRef<HTMLDivElement, KpiPaperProps>(
  ({ isChild, sx, ...props }, ref) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
      <Paper
        ref={ref}
        {...props}
        sx={{
          maxHeight: '116px',
          display: 'flex',
          padding: 1,
          marginBottom: 1,
          justifyContent: 'space-between',
          flexDirection: 'column',
          minWidth: isMobile ? (isChild ? '240px' : '100%') : isChild ? '200px' : '280px',
          boxShadow: 'none',
          border: `1px solid ${theme.palette.divider}`,
          flex: isMobile
            ? isChild
              ? '1 0 240px'
              : '1 0 100%'
            : isChild
              ? '1 0 200px'
              : '1 0 280px',
          ...sx,
        }}
      />
    );
  },
);
KpiPaper.displayName = 'KpiPaper';

// Helper to determine trend icon, color, and tooltip based on sentiment and values
function getTrendDisplay({
  difference,
  sentiment,
  priorValue,
  hasDateRange = false,
  theme,
  t,
}: {
  difference: number;
  sentiment: 'standard' | 'opposite' | '';
  priorValue: string | number;
  hasDateRange: boolean;
  theme: Theme;
  t: TFunction;
}) {
  const colors = {
    error: theme.blox?.marketing?.error,
    success: theme.blox?.marketing?.secondary,
    neutral: theme.palette.grey[500],
  };

  if (difference === 0) {
    return {
      icon: (
        <RemoveRoundedIcon
          sx={{
            color: colors.neutral,
            fontSize: 'clamp(24px, 5vw, ' + (hasDateRange ? '44px' : '54px') + ')',
          }}
          data-testid="trend-neutral"
        />
      ),
      color: colors.neutral,
      tooltip: t('blox.dashboards.kpis.trend_sprites.neutral', { priorValue }),
    };
  }

  // Standard: positive = green up, negative = red down
  // Opposite: positive = red up, negative = green down
  const isPositive = difference > 0;
  let icon, color, tooltip;

  if (sentiment === 'opposite') {
    if (isPositive) {
      icon = (
        <TrendingUpRoundedIcon
          sx={{
            color: colors.error,
            fontSize: 'clamp(24px, 5vw, ' + (hasDateRange ? '44px' : '54px') + ')',
          }}
          data-testid="trend-up"
        />
      );
      color = colors.error;
      tooltip = t('blox.dashboards.kpis.trend_sprites.up', { priorValue });
    } else {
      icon = (
        <TrendingDownRoundedIcon
          sx={{
            color: colors.success,
            fontSize: 'clamp(24px, 5vw, ' + (hasDateRange ? '44px' : '54px') + ')',
          }}
          data-testid="trend-down"
        />
      );
      color = colors.success;
      tooltip = t('blox.dashboards.kpis.trend_sprites.down', { priorValue });
    }
  } else {
    // standard or fallback
    if (isPositive) {
      icon = (
        <TrendingUpRoundedIcon
          sx={{
            color: colors.success,
            fontSize: 'clamp(24px, 5vw, ' + (hasDateRange ? '44px' : '54px') + ')',
          }}
          data-testid="trend-up"
        />
      );
      color = colors.success;
      tooltip = t('blox.dashboards.kpis.trend_sprites.up', { priorValue });
    } else {
      icon = (
        <TrendingDownRoundedIcon
          sx={{
            color: colors.error,
            fontSize: 'clamp(24px, 5vw, ' + (hasDateRange ? '44px' : '54px') + ')',
          }}
          data-testid="trend-down"
        />
      );
      color = colors.error;
      tooltip = t('blox.dashboards.kpis.trend_sprites.down', { priorValue });
    }
  }

  return { icon, color, tooltip };
}

const KPI = ({
  dateRange,
  hasEqualHeightColumns = false,
  isError,
  isLoading,
  kpi,
  showTrend = true,
  sx,
  viewportRef,
  widget,
  isMasked = false,
}: TKpiProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { isDashboardMasked, isChild } = useDashboardContext();
  const isHashTarget = useWidgetHashTarget(widget?.id);

  const isCurrentValueNaN = isNaN(Number(kpi?.currentValue));

  const showWidgetError: boolean =
    (!kpi?.isComplete
      ? false
      : (!kpi?.value && !!kpi?.isComplete) ||
        !kpi?.hasResults ||
        !!kpi?.hasNullResults) || false;

  // Get trend display info
  const trendDisplay =
    showTrend && !kpi?.isLabelKpi && typeof kpi?.difference === 'number'
      ? getTrendDisplay({
          difference: kpi.difference,
          sentiment: kpi.sentiment,
          priorValue: kpi.priorValue,
          hasDateRange: !!dateRange,
          theme,
          t,
        })
      : null;

  if (isMasked && !isDashboardMasked) {
    return (
      <KpiPaper
        id={`widget-${widget?.id}`}
        ref={viewportRef}
        data-testid="kpi-masked"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          height: !!hasEqualHeightColumns ? '100%' : 'auto',
          minHeight: 90,
          ...sx,
          ...(isHashTarget && { border: `2px solid ${bloxMarketingColors.primary}` }),
        }}
      >
        <WidgetMask
          isKpi
          title={widget?.title}
        />
      </KpiPaper>
    );
  }

  return (
    <KpiPaper
      id={`widget-${widget?.id}`}
      ref={viewportRef}
      data-testid="kpi-container"
      isChild={isChild}
      sx={{
        height: !!hasEqualHeightColumns ? '100%' : 'auto',
        minHeight: 90,
        ...sx,
        ...(isHashTarget && { border: `2px solid ${bloxMarketingColors.primary}` }),
      }}
    >
      <WidgetHeader
        title={widget?.title}
        testIdPrefix="kpi"
        dateRange={dateRange}
        headingSx={{
          fontSize: '1rem',
          py: 0,
        }}
        allowExpand={false}
        isHidden={false}
      />

      {isLoading ? (
        <WidgetLoadingIndicator
          iconSize={20}
          height={44}
          data-testid="kpi-loading"
        />
      ) : isError ? (
        <WidgetError
          isMaxHeight={false}
          data-testid="kpi-error"
        />
      ) : (
        <Stack
          data-testid="kpi-content"
          sx={{
            alignItems: 'center',
            flexDirection: 'row',
            justifyContent: 'space-between',
            overflow: 'hidden',
            flex: 1,
            gap: 1,
          }}
        >
          {/* KPI value. */}
          {showWidgetError ? (
            <WidgetError
              type={
                !kpi?.hasResults ||
                !!kpi?.hasNullResults ||
                isCurrentValueNaN ||
                (!!kpi?.hasResults && !kpi?.currentValue)
                  ? 'noData'
                  : 'error'
              }
              isMaxHeight={false}
              data-testid="kpi-widget-error"
            />
          ) : (
            <Tooltip
              title={kpi?.tooltipText}
              placement="top"
              disableInteractive
            >
              <Typography
                variant="h5"
                data-testid="kpi-value"
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '36px',
                  textTransform: 'capitalize',
                  flexShrink: 0,
                }}
              >
                {kpi?.displayText}
                {!!kpi?.subtext?.length && (
                  <Typography
                    component="span"
                    data-testid="kpi-subtext"
                    sx={{
                      fontWeight: 400,
                      fontSize: 14,
                      lineHeight: 1.5,
                      fontStyle: 'italic',
                      textTransform: 'none',
                      ml: 2,
                    }}
                  >
                    {kpi?.subtext}
                  </Typography>
                )}
              </Typography>
            </Tooltip>
          )}

          {/* Render the trend arrow and percent change */}
          {showTrend && !kpi?.isLabelKpi && (
            <Stack
              direction="row"
              data-testid="kpi-trend-container"
              sx={{
                alignItems: 'flex-end',
                justifyContent: 'flex-end',
                position: 'relative',
                height: !dateRange ? '100%' : 'auto',
                flexShrink: 1,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {/* Arrow or dash */}
              {trendDisplay && (
                <Tooltip
                  placement="top"
                  title={trendDisplay.tooltip}
                >
                  <Box
                    data-testid="kpi-trend-icon"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {trendDisplay.icon}
                  </Box>
                </Tooltip>
              )}

              {/* Percentage in difference (trend) */}
              {!!kpi?.value && !!kpi?.percentageChange && (
                <Tooltip
                  data-testid="kpi-trend-tooltip"
                  title={
                    kpi.value === null || kpi.isPriorValueNull
                      ? `An unknown error occurred, not enough data to determine percent change.`
                      : trendDisplay?.tooltip || 'Trend'
                  }
                  placement="top"
                >
                  <Typography
                    variant="subtitle1"
                    data-testid="kpi-trend-percent"
                    sx={{
                      ml: 1,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                    }}
                  >
                    {kpi.value === null || kpi.isPriorValueNull ? (
                      <PriorityHighRoundedIcon fontSize="small" />
                    ) : !kpi.isPriorValueNull && kpi.priorValue === 0 ? (
                      '100%'
                    ) : isCurrentValueNaN ? (
                      ''
                    ) : (
                      kpi?.percentageChange
                    )}
                  </Typography>
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      )}
    </KpiPaper>
  );
};

export default KPI;
